import { useEffect, useState } from "react";
import { PraktikumsnachweiseBlock } from "@/components/audit/PraktikumsnachweiseBlock";
import { AlWiederherstellungBlock } from "@/components/audit/AlWiederherstellungBlock";
import { MailSucheBlock } from "@/components/audit/MailSucheBlock";
import { KomplettReparaturBlock } from "@/components/audit/KomplettReparaturBlock";
import { toast } from "sonner";
import { downloadPdf } from "./AnwesenheitTag";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Wrench } from "lucide-react";
import { Vollsicherung } from "./Vollsicherung";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const d10 = (v) => (v ? `${String(v).slice(8, 10)}.${String(v).slice(5, 7)}.${String(v).slice(0, 4)}` : "—");

const useJob = (url) => {
  const [job, setJob] = useState(null);
  const start = async (dry_run) => {
    const { job_id } = (await axios.post(url, { dry_run })).data;
    setJob({ status: "laeuft" });
    const poll = async () => {
      const j = (await axios.get(`${API}/admin/jobs/${job_id}`)).data;
      setJob(j);
      if (j.status === "laeuft") setTimeout(poll, 3000);
    };
    setTimeout(poll, 2000);
  };
  return [job, start];
};

const Liste = ({ titel, items, render, testid }) => items?.length ? (
  <div className="mt-2" data-testid={testid}>
    <p className="font-medium">{titel} ({items.length})</p>
    <ul className="ml-4 list-disc">{items.map((x, i) => <li key={i}>{render(x)}</li>)}</ul>
  </div>
) : null;

const AnlageUpload = ({ nr, label, vorhanden, onDone }) => {
  const [busy, setBusy] = useState(false);
  const hoch = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("nr", nr);
    fd.append("file", f);
    try {
      await axios.post(`${API}/azav/apv-antwortpaket-schmitz/anlage`, fd);
      toast.success(`Anlage ${nr} hochgeladen`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hochladen fehlgeschlagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 text-sm border rounded px-3 py-2" data-testid={`apv-anlage-${nr}`}>
      <span>Anlage {nr}: {label} · {vorhanden
        ? <span className="text-emerald-700" data-testid={`apv-anlage-${nr}-status`}>hochgeladen: {vorhanden.filename} ({vorhanden.hochgeladen_von})</span>
        : <span className="text-orange-700" data-testid={`apv-anlage-${nr}-status`}>fehlt noch</span>}</span>
      <label className="cursor-pointer text-xs font-medium px-3 py-1.5 rounded border hover:bg-stone-50">
        {busy ? "Lädt…" : vorhanden ? "Ersetzen" : "Hochladen"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="hidden" onChange={hoch} disabled={busy} data-testid={`apv-anlage-${nr}-input`} />
      </label>
    </div>
  );
};

const PruefungenAbgleich = () => {
  const [busy, setBusy] = useState(false);
  const abzug = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pruefungen-abgleich/vorher-abzug`);
      toast.success("Vorher-Abzug gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Vorher-Abzug"); } finally { setBusy(false); }
  };
  return (
    <div className="border rounded-lg p-4 mb-3 flex items-center justify-between gap-3" data-testid="pruefungen-abgleich-block">
      <div>
        <p className="font-medium">Prüfungen zusammenführen – Schritt 1: Widerspruchsliste</p>
        <p className="text-sm text-muted-foreground">Vergleicht Prüfungen-Seite, Stammdaten, Kurseinträge und hochgeladene Unterlagen. Nur lesen.</p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={abzug} data-testid="pruefungen-vorher-abzug">Vorher-Abzug speichern</Button>
        <Button size="sm" variant="outline" data-testid="pruefungen-abgleich-pdf"
          onClick={() => downloadPdf("/pruefungen-abgleich/pdf", "Pruefungen_Widerspruchsliste.pdf")}>Widerspruchsliste (PDF)</Button>
      </div>
    </div>
  );
};

const AntwortPaket = () => {
  const [laedt, setLaedt] = useState(false);
  const [anl, setAnl] = useState({ anlagen: [], erwartet: {} });
  const ladeAnlagen = () => axios.get(`${API}/azav/apv-antwortpaket-schmitz/anlagen`).then((r) => setAnl(r.data)).catch(() => {});
  useEffect(() => { ladeAnlagen(); }, []);
  const laden = async () => {
    setLaedt(true);
    try {
      const r = await axios.get(`${API}/azav/apv-antwortpaket-schmitz-zip`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "APV_Antwort_Frau_Schmitz_Anlagen.zip";
      a.click();
    } finally {
      setLaedt(false);
    }
  };
  return (
    <div className="border rounded-lg p-4 space-y-2" data-testid="apv-antwortpaket-block">
      <p className="font-medium">APV-Antwort Frau Schmitz – Mailtext + Anlagen 1–11 als ZIP</p>
      <p className="text-sm text-muted-foreground">Enthält die Datei „00_BITTE_PRUEFEN“ mit allem, was noch fehlt. Fehlende Anlagen hier hochladen, dann das Paket neu laden.</p>
      {Object.entries(anl.erwartet).map(([nr, label]) => (
        <AnlageUpload key={nr} nr={nr} label={label} vorhanden={anl.anlagen.find((x) => x.nr === nr)} onDone={ladeAnlagen} />
      ))}
      <Button size="sm" onClick={laden} disabled={laedt} data-testid="apv-antwortpaket-download">{laedt ? "Wird erstellt…" : "Antwortpaket herunterladen"}</Button>
    </div>
  );
};

const SammelPdf = ({ id }) => {
  const laden = async () => {
    const r = await axios.get(`${API}/admin/sammel-pdf/${id}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "PHK_Zertifikate_2026_Berichtigung_Sammel.pdf";
    a.click();
  };
  return <Button size="sm" className="mt-2" onClick={laden} data-testid="azav-zert-sammel-pdf">Sammel-PDF für Edelpapier herunterladen</Button>;
};

const JobBlock = ({ titel, url, prefix, children }) => {
  const [job, start] = useJob(url);
  const busy = job?.status === "laeuft";
  return (
    <div className="border rounded p-3 mb-3 text-sm" data-testid={`${prefix}-block`}>
      <p className="font-medium mb-2">{titel}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => start(true)} data-testid={`${prefix}-pruefen`}>Erst prüfen</Button>
        <Button size="sm" disabled={busy || !job?.ergebnis} className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
          onClick={() => window.confirm("Jetzt wirklich im CRM reparieren? Alte Dateien werden archiviert.") && start(false)} data-testid={`${prefix}-reparieren`}>Jetzt reparieren</Button>
      </div>
      {busy && <p className="mt-2 text-stone-500" data-testid={`${prefix}-laeuft`}>Läuft … das kann einige Minuten dauern.</p>}
      {job?.status === "fehler" && <p className="mt-2 text-red-700" data-testid={`${prefix}-fehler`}>{job.fehler}</p>}
      {job?.ergebnis && <div className="mt-2 text-xs" data-testid={`${prefix}-ergebnis`}>
        <p className="text-stone-500">{job.dry_run ? "Nur geprüft – noch nichts geändert." : "Repariert."}</p>
        {children(job.ergebnis)}
      </div>}
    </div>
  );
};

export const AzavReparaturen = () => (
  <>
  <Vollsicherung />
  <Card className="card-custom mb-6" data-testid="azav-reparaturen-card">
    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wrench size={18} /> AZAV-Reparaturen</CardTitle></CardHeader>
    <CardContent className="pt-0">
      <JobBlock titel="Unterschriebene Anwesenheitslisten 2026 neu erzeugen (Praktikum separat, Original-Unterschriftsdatum bleibt)" url={`${API}/admin/anwesenheitslisten-2026-neu`} prefix="azav-al">
        {(e) => <>
          {!e.neu?.length && !e.praktikum_fehlt?.length && !e.fehler?.length && <p data-testid="azav-al-leer">Keine unterschriebenen Anwesenheitslisten aus 2026 gefunden.</p>}
          <Liste titel={e.dry_run ? "Wird neu erzeugt" : "Neu erzeugt"} items={e.neu} testid="azav-al-neu"
            render={(x) => `${x.name} · ${x.kurs || "—"} · unterschrieben ${d10(x.unterschrieben_am)}${x.praktikum ? ` · Praktikum ${x.praktikum}` : ""}`} />
          <Liste titel="Praktikumsdaten fehlen – bitte Praktikum Beginn/Ende beim Teilnehmer eintragen" items={e.praktikum_fehlt} testid="azav-al-praktikum-fehlt"
            render={(x) => `${x.name} · ${x.kurs || "—"}`} />
          <Liste titel="Nicht möglich" items={e.fehler} testid="azav-al-fehler" render={(x) => `${x.name} · ${x.grund}`} />
        </>}
      </JobBlock>
      <PruefungenAbgleich />
      <div className="border rounded-lg p-4 mb-3 flex items-center justify-between gap-3" data-testid="fokusliste-block">
        <div>
          <p className="font-medium">Fokusliste Prüfung / Zertifikat / Anwesenheit</p>
          <p className="text-sm text-muted-foreground">A) alle Bildungsgutschein-Fälle mit beendetem Kurs · B) übrige (ohne Rezi) mit Zertifikat, aber ohne Prüfungsunterlage oder Anwesenheitsliste. Nur lesen.</p>
        </div>
        <Button size="sm" data-testid="fokusliste-download" onClick={() => downloadPdf("/azav/fokusliste/pdf", "Fokusliste_Pruefung_Zertifikat.pdf")}>PDF laden</Button>
      </div>
      <KomplettReparaturBlock />
      <MailSucheBlock />
      <AlWiederherstellungBlock />
      <PraktikumsnachweiseBlock />
      <div className="border rounded-lg p-4 mb-3 flex items-center justify-between gap-3" data-testid="phk-pruefbericht-block">
        <div>
          <p className="font-medium">Prüfbericht PHK (1232) Bildungsgutschein ab 2025 – zuerst ausführen</p>
          <p className="text-sm text-muted-foreground">Zählt jede Doppel- und Fehlerquelle (Personen, Finanzierung, Kurse, Dokumente, Prüfungen, Anwesenheit, Zertifikatsnummern). Erst bei 0 ist die Fehlliste verlässlich. Nur lesen.</p>
        </div>
        <Button size="sm" variant="outline" data-testid="phk-pruefbericht-download"
          onClick={() => downloadPdf("/azav/phk-bg-pruefbericht/pdf", "Pruefbericht_PHK_BG_ab_2025.pdf")}>PDF laden</Button>
      </div>
      <div className="border rounded-lg p-4 mb-3 flex items-center justify-between gap-3" data-testid="audit-checkliste-block">
        <div>
          <p className="font-medium">Audit-Checkliste Bildungsgutschein-Maßnahmen (für Linda Lüppe)</p>
          <p className="text-sm text-muted-foreground">Regeln + Lückenliste je Teilnehmer/in der Maßnahmen 1232, 1013, 1045. Rezis ausgenommen (keine Prüfung). Nur lesen.</p>
        </div>
        <Button size="sm" variant="outline" data-testid="audit-checkliste-download"
          onClick={() => downloadPdf("/azav/audit-checkliste/pdf", "Audit-Checkliste_BG-Massnahmen.pdf")}>Alle 3 Maßnahmen</Button>
        <Button size="sm" data-testid="audit-checkliste-phk-download"
          onClick={() => downloadPdf("/azav/audit-checkliste/pdf?massnahme=1232&ab=2025-01-01", "Fehlliste_PHK_BG_ab_2025.pdf")}>Nur PHK (1232) ab 2025</Button>
      </div>
      <AntwortPaket />
      <JobBlock titel="PHK-Zertifikate 2026 mit Praktikumszeile berichtigen (gleiche Nr. + Original-Ausstellungsdatum)" url={`${API}/admin/phk-zertifikate-2026-praktikum`} prefix="azav-zert">
        {(e) => <>
          {!e.neu?.length && !e.bereits?.length && !e.fehler?.length && <p data-testid="azav-zert-leer">Keine PHK-Zertifikate aus 2026 gefunden.</p>}
          <Liste titel={e.dry_run ? "Wird berichtigt" : "Berichtigt"} items={e.neu} testid="azav-zert-neu"
            render={(x) => `${x.name} · Nr. ${x.nummer} · ausgestellt ${d10(x.ausgestellt_am)}`} />
          <Liste titel="Bereits berichtigt" items={e.bereits} testid="azav-zert-bereits" render={(x) => `${x.name} · Nr. ${x.nummer}`} />
          <Liste titel="Nicht möglich" items={e.fehler} testid="azav-zert-fehler" render={(x) => `${x.name} · Nr. ${x.nummer} · ${x.grund}`} />
          {e.sammel_pdf_id && <SammelPdf id={e.sammel_pdf_id} />}
        </>}
      </JobBlock>
      <JobBlock titel="GF-Unterschrift vor dem 12.10.2026 auf Markus Dickie umstellen" url={`${API}/admin/gf-unterschrift-korrigieren`} prefix="azav-gf">
        {(e) => <>
          {!e.korrigiert?.length && !e.nur_liste?.length && <p data-testid="azav-gf-leer">Keine Dokumente mit falscher GF-Unterschrift gefunden.</p>}
          <Liste titel={e.dry_run ? "Wird umgestellt" : "Umgestellt"} items={e.korrigiert} testid="azav-gf-korrigiert"
            render={(x) => `${x.name} · ${x.kategorie || "—"} · ${x.datei} · Datum ${d10(x.dokumentdatum)}`} />
          <Liste titel="Bitte manuell prüfen (z. B. Zertifikate, Sonstiges)" items={e.nur_liste} testid="azav-gf-manuell"
            render={(x) => `${x.name} · ${x.kategorie || "—"} · ${x.datei} · Datum ${d10(x.dokumentdatum)}${x.hinweis ? ` · ${x.hinweis}` : ""}`} />
        </>}
      </JobBlock>
      <JobBlock titel="Verlorene Unterschriften (Vertrag / Datenschutz / Erstgespräch) wiederherstellen" url={`${API}/admin/unterschriften-wiederherstellen-alle`} prefix="azav-sig">
        {(e) => <>
          {!e.repariert?.length && !e.nicht_moeglich?.length && <p data-testid="azav-sig-leer">Keine Teilnehmenden mit verlorener Unterschrift gefunden.</p>}
          <Liste titel="Wiederherstellbar / wiederhergestellt" items={e.repariert} testid="azav-sig-repariert"
            render={(x) => `${x.name} · ${x.kategorien.join(", ")} · unterschrieben ${d10(x.unterschrieben_am)}`} />
          <Liste titel="Nicht möglich – Teilnehmende muss neu unterschreiben" items={e.nicht_moeglich} testid="azav-sig-nicht-moeglich"
            render={(x) => `${x.name} · ${x.grund}`} />
          <Liste titel="Achtung: laut Protokoll nach Maßnahmebeginn unterschrieben" items={e.unterschrieben_nach_beginn} testid="azav-sig-nach-beginn"
            render={(x) => `${x.name} · unterschrieben ${d10(x.unterschrieben_am)} · Beginn ${d10(x.massnahmebeginn)}`} />
        </>}
      </JobBlock>
      <JobBlock titel="Teilnahmebescheinigungen mit falscher Trägernummer korrigieren" url={`${API}/admin/teilnahmebescheinigungen-korrigieren`} prefix="azav-tb">
        {(e) => e.liste?.length ? <Liste titel="Betroffene Teilnehmende" items={e.liste} testid="azav-tb-liste"
          render={(x) => `${x.name} · ausgestellt ${d10(x.ausstellungsdatum)} · ${x.email || "keine E-Mail"} · ${x.quelle}`} />
          : <p data-testid="azav-tb-leer">Keine betroffenen Teilnahmebescheinigungen gefunden.</p>}
      </JobBlock>
    </CardContent>
  </Card>
  </>
);
